import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { ActionButton } from '@communityshare/ui-shared';
import {
  useCreateVideo,
  usePrefillVideo,
  useRefreshVideo,
  useUpdateVideo
} from '@/features/staff-settings-videos/hooks/useStaffVideos';
import useIntentTagIds from '@/features/staff-settings-videos/hooks/useIntentTagIds';
import useSingleFlight from '@/features/staff-settings-videos/hooks/useSingleFlight';
import getErrorMessage from '@/features/staff-settings-videos/api/errorMessages';
import validateVideoForm from '@/features/staff-settings-videos/utils/validateVideoForm';
import formatDuration from '@/features/videos-common/utils/formatDuration';
import { INTENT_TAG_NAMES } from '@/features/videos-common/audiences';
import FeatureDialog from './FeatureDialog';
import FormRow, { inputClassName } from './FormRow';
import StatusMessage from './StatusMessage';
import VideoThumbnail from './VideoThumbnail';

const FIELD_ORDER = ['url', 'title', 'resourceUrl'];

function initialValues(video) {
  return {
    url: '',
    title: video?.title ?? '',
    description: video?.description ?? '',
    transcript: video?.transcript ?? '',
    resourceTitle: video?.resourceTitle ?? '',
    resourceDescription: video?.resourceDescription ?? '',
    resourceUrl: video?.resourceUrl ?? '',
    isPublished: Boolean(video?.isPublished),
    audiences: (video?.audiences || []).map((tag) => tag.id).filter(Number.isInteger)
  };
}

function initialYouTubeDetails(video) {
  if (!video) return null;
  return {
    youtubeVideoId: video.youtubeVideoId,
    durationSeconds: video.durationSeconds,
    thumbnailUrl: video.thumbnailUrl
  };
}

/**
 * Add (video = null) or edit a video.
 *
 * YouTube-owned details (video ID, duration, thumbnail) are shown read-only.
 * They come from prefill (add) or refresh (edit) and are never part of the
 * request body: submits go through toVideoInput, which whitelists only the
 * curated StaffVideoInput fields.
 */
VideoFormDialog.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.number.isRequired,
    youtubeVideoId: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    transcript: PropTypes.string,
    resourceTitle: PropTypes.string,
    resourceDescription: PropTypes.string,
    resourceUrl: PropTypes.string,
    isPublished: PropTypes.bool,
    audiences: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    })),
    durationSeconds: PropTypes.number,
    thumbnailUrl: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
};

export default function VideoFormDialog(props) {
  const { video = null, onClose, onSaved } = props;
  const { t } = useTranslation();
  const mode = video ? 'edit' : 'create';

  const [values, setValues] = useState(() => initialValues(video));
  const [details, setDetails] = useState(() => initialYouTubeDetails(video));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [youtubeStatus, setYoutubeStatus] = useState(null);

  const prefill = usePrefillVideo();
  const refresh = useRefreshVideo();
  const createVideo = useCreateVideo();
  const updateVideo = useUpdateVideo();
  const intentTags = useIntentTagIds();

  const fieldRefs = useRef({});
  const firstFieldRef = useRef(null);

  const saving = createVideo.isLoading || updateVideo.isLoading;
  const busy = saving || prefill.isLoading || refresh.isLoading;

  function setField(name, value) {
    setValues((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current) => {
        const next = { ...current };
        delete next[name];
        return next;
      });
    }
  }

  // Tag ids by name. The video's own tags fill in while the tag list loads.
  const tagIds = useMemo(() => {
    const ids = {};
    (video?.audiences || []).forEach((tag) => {
      if (INTENT_TAG_NAMES.includes(tag.name)) ids[tag.name] = tag.id;
    });
    return { ...ids, ...intentTags.ids };
  }, [video, intentTags.ids]);
  const tagsMissing = !intentTags.isLoading && INTENT_TAG_NAMES.some((name) => !tagIds[name]);

  function toggleAudience(id, checked) {
    setValues((current) => {
      const others = current.audiences.filter((existing) => existing !== id);
      return { ...current, audiences: checked ? [...others, id] : others };
    });
  }

  function registerField(name) {
    return (node) => {
      fieldRefs.current[name] = node;
      if (name === FIELD_ORDER[mode === 'create' ? 0 : 1]) firstFieldRef.current = node;
    };
  }

  function handleUrlChange(value) {
    setField('url', value);
    // A different URL invalidates the details fetched for the old one.
    if (mode === 'create' && details) {
      setDetails(null);
      setYoutubeStatus(null);
    }
  }

  const handleFetchDetails = useSingleFlight(async () => {
    setYoutubeStatus(null);
    if (!values.url.trim()) {
      setErrors((current) => ({ ...current, url: 'staff_videos.form.validation.url_required' }));
      fieldRefs.current.url?.focus();
      return;
    }
    try {
      const result = await prefill.mutateAsync(values.url);
      setDetails({
        youtubeVideoId: result.youtubeVideoId,
        durationSeconds: result.durationSeconds,
        thumbnailUrl: result.thumbnailUrl
      });
      setValues((current) => ({
        ...current,
        title: result.title ?? current.title,
        description: current.description.trim() ? current.description : (result.descriptionSuggestion ?? '')
      }));
      setErrors((current) => {
        const next = { ...current };
        delete next.url;
        delete next.title;
        return next;
      });
      setYoutubeStatus({ tone: 'success', title: t('staff_videos.form.fetched') });
    } catch (error) {
      const { message, detail } = getErrorMessage(error, t, 'prefill');
      setYoutubeStatus({ tone: 'error', title: message, detail });
    }
  });

  const handleRefresh = useSingleFlight(async () => {
    setYoutubeStatus(null);
    try {
      const result = await refresh.mutateAsync(video.id);
      // Only duration and thumbnail change; curated fields in the form stay as typed.
      setDetails((current) => ({
        ...current,
        durationSeconds: result.durationSeconds,
        thumbnailUrl: result.thumbnailUrl
      }));
      setYoutubeStatus({ tone: 'success', title: t('staff_videos.form.refreshed') });
    } catch (error) {
      const { message, detail } = getErrorMessage(error, t, 'youtube');
      setYoutubeStatus({ tone: 'error', title: message, detail });
    }
  });

  const handleSubmit = useSingleFlight(async () => {
    setSubmitError(null);
    const nextErrors = validateVideoForm(values, {
      mode,
      youtubeVideoId: details?.youtubeVideoId
    });
    setErrors(nextErrors);

    const firstInvalid = FIELD_ORDER.find((name) => nextErrors[name]);
    if (firstInvalid) {
      fieldRefs.current[firstInvalid]?.focus();
      return;
    }

    try {
      if (mode === 'create') {
        await createVideo.mutateAsync({ youtubeVideoId: details.youtubeVideoId, values });
        onSaved(t('staff_videos.form.created'));
      } else {
        await updateVideo.mutateAsync({ id: video.id, values });
        onSaved(t('staff_videos.form.saved'));
      }
    } catch (error) {
      const { message, detail } = getErrorMessage(error, t, mode === 'create' ? 'youtube' : 'default');
      setSubmitError({ title: message, detail });
    }
  });

  const optional = t('staff_videos.form.optional');
  const notAvailable = t('staff_videos.form.not_available');
  const duration = formatDuration(details?.durationSeconds);

  return (
    <FeatureDialog
      title={mode === 'create' ? t('staff_videos.form.add_title') : t('staff_videos.form.edit_title')}
      onClose={saving ? () => {} : onClose}
      initialFocus={firstFieldRef}
    >
      <form
        noValidate
        className="mt-6 space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        {mode === 'create' && (
          <FormRow
            id="staff-video-url"
            label={t('staff_videos.form.youtube_url')}
            required
            help={t('staff_videos.form.youtube_url_help')}
            error={errors.url && t(errors.url)}
          >
            {(aria) => (
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  {...aria}
                  ref={registerField('url')}
                  type="url"
                  inputMode="url"
                  autoComplete="off"
                  value={values.url}
                  onChange={(event) => handleUrlChange(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleFetchDetails();
                    }
                  }}
                  disabled={busy}
                  className={inputClassName(Boolean(errors.url))}
                />
                <ActionButton
                  variant="secondary"
                  loading={prefill.isLoading}
                  loadingLabel={t('staff_videos.form.fetching')}
                  disabled={busy}
                  onClick={handleFetchDetails}
                >
                  {t('staff_videos.form.fetch_details')}
                </ActionButton>
              </div>
            )}
          </FormRow>
        )}

        <div aria-live="polite">
          {youtubeStatus && (
            <StatusMessage tone={youtubeStatus.tone} title={youtubeStatus.title} detail={youtubeStatus.detail} />
          )}
        </div>

        {details && (
          <div className="flex flex-col sm:flex-row gap-4 bg-cs-gray-50 rounded-cs-10 p-4">
            <VideoThumbnail
              url={details.thumbnailUrl}
              alt={t('staff_videos.list.thumbnail_alt', { title: values.title || details.youtubeVideoId })}
              size="medium"
            />
            <div className="flex flex-col gap-3">
              <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm content-start">
                <dt className="font-semibold text-cs-gray-900">{t('staff_videos.form.youtube_id')}</dt>
                <dd className="text-cs-gray-700 font-mono break-all">{details.youtubeVideoId}</dd>
                <dt className="font-semibold text-cs-gray-900">{t('staff_videos.form.duration')}</dt>
                <dd className="text-cs-gray-700 tabular-nums">{duration || notAvailable}</dd>
              </dl>
              {mode === 'edit' && (
                <ActionButton
                  variant="tertiary"
                  loading={refresh.isLoading}
                  loadingLabel={t('staff_videos.form.refreshing')}
                  disabled={busy}
                  onClick={handleRefresh}
                >
                  <span className="inline-flex items-center gap-1">
                    <ArrowPathIcon className="size-4" aria-hidden="true" />
                    {t('staff_videos.form.refresh')}
                  </span>
                </ActionButton>
              )}
            </div>
          </div>
        )}

        <FormRow
          id="staff-video-title"
          label={t('staff_videos.form.title')}
          required
          error={errors.title && t(errors.title)}
        >
          {(aria) => (
            <input
              {...aria}
              ref={registerField('title')}
              type="text"
              value={values.title}
              onChange={(event) => setField('title', event.target.value)}
              disabled={saving}
              className={inputClassName(Boolean(errors.title))}
            />
          )}
        </FormRow>

        <FormRow
          id="staff-video-description"
          label={t('staff_videos.form.description')}
          optionalLabel={optional}
          help={t('staff_videos.form.description_help')}
        >
          {(aria) => (
            <textarea
              {...aria}
              rows={3}
              value={values.description}
              onChange={(event) => setField('description', event.target.value)}
              disabled={saving}
              className={inputClassName(false)}
            />
          )}
        </FormRow>

        <FormRow
          id="staff-video-transcript"
          label={t('staff_videos.form.transcript')}
          optionalLabel={optional}
        >
          {(aria) => (
            <textarea
              {...aria}
              rows={6}
              value={values.transcript}
              onChange={(event) => setField('transcript', event.target.value)}
              disabled={saving}
              className={inputClassName(false)}
            />
          )}
        </FormRow>

        <FormRow
          id="staff-video-resource-title"
          label={t('staff_videos.form.resource_title')}
          optionalLabel={optional}
        >
          {(aria) => (
            <input
              {...aria}
              type="text"
              value={values.resourceTitle}
              onChange={(event) => setField('resourceTitle', event.target.value)}
              disabled={saving}
              className={inputClassName(false)}
            />
          )}
        </FormRow>

        <FormRow
          id="staff-video-resource-description"
          label={t('staff_videos.form.resource_description')}
          optionalLabel={optional}
        >
          {(aria) => (
            <textarea
              {...aria}
              rows={2}
              value={values.resourceDescription}
              onChange={(event) => setField('resourceDescription', event.target.value)}
              disabled={saving}
              className={inputClassName(false)}
            />
          )}
        </FormRow>

        <FormRow
          id="staff-video-resource-url"
          label={t('staff_videos.form.resource_url')}
          optionalLabel={optional}
          error={errors.resourceUrl && t(errors.resourceUrl)}
        >
          {(aria) => (
            <input
              {...aria}
              ref={registerField('resourceUrl')}
              type="url"
              inputMode="url"
              value={values.resourceUrl}
              onChange={(event) => setField('resourceUrl', event.target.value)}
              disabled={saving}
              className={inputClassName(Boolean(errors.resourceUrl))}
            />
          )}
        </FormRow>

        <fieldset className="space-y-2" aria-describedby="staff-video-audience-help">
          <legend className="text-sm font-semibold text-cs-gray-900">
            {t('staff_videos.form.audience')} <span className="font-normal text-cs-gray-500">{optional}</span>
          </legend>
          <p id="staff-video-audience-help" className="text-sm text-cs-gray-500">
            {t('staff_videos.form.audience_help')}
            {tagsMissing && ` ${t('staff_videos.form.audience_unavailable')}`}
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {INTENT_TAG_NAMES.map((name) => {
              const id = tagIds[name];
              const inputId = `staff-video-audience-${name.toLowerCase()}`;
              return (
                <div key={name} className="flex items-center gap-2">
                  <input
                    id={inputId}
                    type="checkbox"
                    checked={Boolean(id) && values.audiences.includes(id)}
                    onChange={(event) => toggleAudience(id, event.target.checked)}
                    disabled={saving || !id}
                    className="h-5 w-5 rounded border-cs-gray-300 text-cs-blue-500 focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-1"
                  />
                  <label htmlFor={inputId} className="text-sm text-cs-gray-900">
                    {t(`staff_videos.audiences.${name.toLowerCase()}`)}
                  </label>
                </div>
              );
            })}
          </div>
        </fieldset>

        <div className="flex items-start gap-3">
          <input
            id="staff-video-published"
            type="checkbox"
            checked={values.isPublished}
            onChange={(event) => setField('isPublished', event.target.checked)}
            disabled={saving}
            aria-describedby="staff-video-published-help"
            className="mt-1 h-5 w-5 rounded border-cs-gray-300 text-cs-blue-500 focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-1"
          />
          <div className="flex flex-col">
            <label htmlFor="staff-video-published" className="text-sm font-semibold text-cs-gray-900">
              {t('staff_videos.form.published')}
            </label>
            <p id="staff-video-published-help" className="text-sm text-cs-gray-500">
              {t('staff_videos.form.published_help')}
            </p>
          </div>
        </div>

        <div aria-live="assertive">
          {submitError && (
            <StatusMessage tone="error" title={submitError.title} detail={submitError.detail} />
          )}
          {!submitError && Object.keys(errors).length > 0 && (
            <StatusMessage tone="error" title={t('staff_videos.form.error_summary')} />
          )}
        </div>

        <div className="flex flex-col-reverse md:flex-row md:justify-between items-stretch md:items-center gap-3 pt-2">
          <ActionButton variant="secondary" onClick={onClose} disabled={saving}>
            {t('staff_videos.form.cancel')}
          </ActionButton>
          <ActionButton
            type="submit"
            variant="primary"
            loading={saving}
            loadingLabel={t('staff_videos.form.saving')}
            disabled={busy}
          >
            {mode === 'create' ? t('staff_videos.form.create') : t('staff_videos.form.save')}
          </ActionButton>
        </div>
      </form>
    </FeatureDialog>
  );
}
